import React, { ComponentProps, forwardRef, useCallback, useMemo, useState } from "react";
import {
    TextInput,
    TextInputProps,
    StyleSheet,
    PixelRatio,
    StyleProp,
    TextStyle,
    Platform,
} from "react-native";
import { useTheme } from "../hooks/use-theme";
import { useMaskedInputProps } from "react-native-mask-input";
import { Mask } from "react-native-mask-input/src/formatWithMask.types";
import { XOR } from "ts-essentials";
import BaseField, { BaseFieldProps } from "./BaseField";

const FONT_SCALE = PixelRatio.getFontScale();

const BASE_PADDING_VERTICAL = 14;
const MULTILINE_PADDING_VERTICAL =
    BASE_PADDING_VERTICAL - (FONT_SCALE - 1) * (BASE_PADDING_VERTICAL / 2);

export type TextFieldProps = Omit<BaseFieldProps, "component" | "hasValue"> &
    Omit<TextInputProps, "theme" | "placeholder"> & {
        mask?: Mask;
        showObfuscatedValue?: boolean;
        placeholderFillCharacter?: string;
        obfuscationCharacter?: string;
        maskAutoComplete?: boolean;
        isRnTextInput?: boolean;
        isTime?: boolean;
        inputStyle?: StyleProp<TextStyle>;
        maxValue?: number;
    } & XOR<XOR<{ isTime?: boolean }, { isHour?: boolean }>, { isDecimalNumber?: boolean }>;

const hourMask: Mask = (text?: string) => {
    const cleanTime = text?.replace(/\D+/g, "");

    const hourFirstDigit = /[012]/;
    let hourSecondDigit = /\d/;

    if (cleanTime?.charAt(0) === "2") {
        hourSecondDigit = /[0123]/;
    }

    const minuteFirstDigit = /[012345]/;
    const minuteSecondDigit = /\d/;

    return [hourFirstDigit, hourSecondDigit, ":", minuteFirstDigit, minuteSecondDigit];
};

const timeMask: Mask = (text = "") => {
    const cleanTime = text.replace(/\D+/g, "");

    const hourFirstDigit = /\d/;
    const hourSecondDigit = /\d/;

    const minuteFirstDigit = /[012345]/;
    const minuteSecondDigit = /\d/;

    return [
        hourFirstDigit,
        ...(cleanTime[0] !== "0" && text.charAt(1) !== ":" ? [hourSecondDigit] : []),
        ":",
        minuteFirstDigit,
        minuteSecondDigit,
    ];
};

const decimalMask: Mask = (text = "") => {
    const parts = text.split(".");
    return [
        ...parts[0].split(""),
        ...(parts.length > 1 ? [".", ...parts[1].split("").slice(0, 2)] : []),
    ];
};

// eslint-disable-next-line react/display-name
const TextField = forwardRef<TextInput, TextFieldProps>(
    (
        {
            style,
            inputStyle,
            mask,
            selection,
            showObfuscatedValue,
            placeholderFillCharacter,
            obfuscationCharacter,
            maskAutoComplete = true,
            onChangeText,
            isTime,
            isHour,
            label,
            disabled,
            value,
            isDecimalNumber,
            left,
            right,
            leftColor,
            rightColor,
            labelLeftPadding,
            error,
            showErrorText,
            onFocus: propsOnFocus,
            onBlur: propsOnBlur,
            placeholder,
            isSelfContained,
            maxValue,
            ...props
        },
        ref,
    ) => {
        const [isFocused, setIsFocused] = useState(false);
        const styles = useStyles();
        const maskedInputProps = useMaskedInputProps({
            value,
            mask: isTime ? timeMask : isHour ? hourMask : isDecimalNumber ? decimalMask : mask,
            maskAutoComplete,
            obfuscationCharacter,
            onChangeText,
            placeholderFillCharacter,
            showObfuscatedValue,
        });

        const onFieldChangeText = useCallback<
            NonNullable<ComponentProps<typeof TextInput>["onChangeText"]>
        >(
            (e) => {
                if (maxValue && isDecimalNumber && parseFloat(e) > maxValue) return;

                maskedInputProps.onChangeText(
                    isTime || isHour
                        ? e.replace(/[.,;-_]/g, ":")
                        : isDecimalNumber
                          ? e.replace(/[,;-_]/g, ".").replace(/-/g, "")
                          : e,
                );
            },
            [isDecimalNumber, isHour, isTime, maskedInputProps, maxValue],
        );

        const onEndEditing = useCallback<
            NonNullable<ComponentProps<typeof TextInput>["onEndEditing"]>
        >(
            (e) => {
                onFieldChangeText(e.nativeEvent.text.trim());
            },
            [onFieldChangeText],
        );

        const onFocus = useCallback(() => {
            setIsFocused(true);
            propsOnFocus?.();
        }, [propsOnFocus]);

        const onBlur = useCallback(() => {
            setIsFocused(false);
            propsOnBlur?.();
        }, [propsOnBlur]);

        return (
            <BaseField
                style={[props.multiline ? styles.multiline : {}, style]}
                value={!!value}
                label={label !== undefined ? label : ""}
                placeholder={placeholder !== undefined ? placeholder : undefined}
                disabled={disabled}
                left={left}
                right={right}
                labelLeftPadding={labelLeftPadding}
                leftColor={leftColor}
                rightColor={rightColor}
                error={error}
                showErrorText={showErrorText}
                onFocus={onFocus}
                onBlur={onBlur}
                isFocused={props.isFocused || isFocused}
                isSelfContained={isSelfContained}
                componentRef={ref}
                component={
                    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                    // @ts-ignore
                    <TextInput
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore  workaround to have the correct selection color on android
                        selectionColor={null}
                        keyboardType={isDecimalNumber ? "decimal-pad" : undefined}
                        selection={maskedInputProps.selection || selection}
                        style={[
                            styles.textInput,
                            error ? styles.errorTextInput : {},
                            disabled && styles.disabledTextInput,
                            props.multiline ? styles.textInputMultiline : {},
                            inputStyle,
                            props.textAlign && { textAlign: props.textAlign }, // Necessary to ensure native/web consistency
                            Platform.OS === "ios" &&
                                !props.multiline && {
                                    lineHeight: null,
                                },
                        ]}
                        onChangeText={onFieldChangeText}
                        onEndEditing={onEndEditing}
                        editable={!disabled}
                        value={value}
                        {...props}
                    />
                }
            />
        );
    },
);

const useStyles = () => {
    const { colors, fonts } = useTheme();

    return useMemo(
        () =>
            StyleSheet.create({
                textInput: {
                    color: colors.primary,
                    ...fonts.regular,
                },
                errorTextInput: {
                    color: colors.error,
                },
                multiline: {
                    height: fonts.regular.fontSize * 8 + MULTILINE_PADDING_VERTICAL,
                    paddingVertical: MULTILINE_PADDING_VERTICAL,
                },
                textInputMultiline: {
                    maxHeight: "100%",
                    minHeight: "100%",
                    textAlignVertical: "top",
                    marginVertical: MULTILINE_PADDING_VERTICAL,
                },
                disabledTextInput: {
                    color: colors.fieldLabel,
                },
            }),
        [colors.error, colors.fieldLabel, colors.primary, fonts.regular],
    );
};

export default TextField;
